import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CardView } from "@breeze/shared";
import type { DispatchBlock, DispatchTech, ScheduleAvailabilityBlock } from "../api";
import {
  createScheduleAvailability,
  deleteScheduleAvailability,
  fetchCard,
  fetchDispatchBoard,
  listScheduleAvailability,
  resolveVisitCard,
} from "../api";
import {
  clampToCol,
  colHeight,
  daysInMonth,
  dayWindow,
  fmtLongDate,
  fmtMonthYear,
  hourLabels,
  isoDate,
  isoHM,
  minutesFromDayStart,
  monthEndIso,
  monthStartIso,
  pad2,
  parseIsoDate,
  shiftDate,
  shiftMonthFirst,
  todayIso,
} from "../lib/dispatchGrid.js";
import { TopNav } from "./TopNav.js";
import { Slideout } from "./Slideout.js";

// ===========================================================================
// Schedule board — port of the legacy breeze.services/schedule.php admin grid.
// Single day, technicians as columns, 30-min time rows. Jobs + free/busy come
// from the dispatch /blocks proxy; availability (editable) from the kanban
// /api/schedule/availability endpoint. Admins add/remove availability and
// click a job to open its card.
// ===========================================================================

const TIME_COL_W = 56; // px
const COL_MIN_W = 175; // px per tech column
const HEADER_H = 64; // px — tech header height (keeps bodies aligned)
const LEGACY_ORDER_BASE = "https://breeze.services/order-form.php?id=";

type Props = { userName: string };

export function Schedule({ userName }: Props): JSX.Element {
  const [date, setDate] = useState<string>(todayIso());
  const [viewMode, setViewMode] = useState<"day" | "month">("day");
  const qc = useQueryClient();

  // Month-view window — recomputed whenever the user navigates by month.
  const monthFrom = useMemo(() => monthStartIso(date), [date]);
  const monthTo = useMemo(() => monthEndIso(date), [date]);
  const monthDayCount = useMemo(() => daysInMonth(date), [date]);

  // Day queries: one day's worth of jobs + availability.
  const dayBoard = useQuery({
    queryKey: ["schedule", "board", date],
    queryFn: () => fetchDispatchBoard(date, 1),
    enabled: viewMode === "day",
  });
  const dayAvail = useQuery({
    queryKey: ["schedule", "avail", date],
    queryFn: () => listScheduleAvailability(date, date),
    enabled: viewMode === "day",
  });

  // Month queries: jobs + availability for the whole calendar month so the
  // Month grid can render a per-day badge per tech without fetching daily.
  const monthBoard = useQuery({
    queryKey: ["schedule", "monthBoard", monthFrom, monthDayCount],
    queryFn: () => fetchDispatchBoard(monthFrom, monthDayCount),
    enabled: viewMode === "month",
  });
  const monthAvail = useQuery({
    queryKey: ["schedule", "monthAvail", monthFrom, monthTo],
    queryFn: () => listScheduleAvailability(monthFrom, monthTo),
    enabled: viewMode === "month",
  });

  // Pick the active query pair so the rest of the component stays simple.
  const board = viewMode === "day" ? dayBoard : monthBoard;
  const avail = viewMode === "day" ? dayAvail : monthAvail;

  const [jobPopover, setJobPopover] = useState<{ block: DispatchBlock } | null>(null);
  const [addFor, setAddFor] = useState<DispatchTech | null>(null);
  const [openCardId, setOpenCardId] = useState<number | null>(null);

  const cardQuery = useQuery({
    queryKey: ["schedule", "card", openCardId],
    queryFn: () => fetchCard(openCardId as number),
    enabled: openCardId !== null,
  });

  const techs = useMemo(() => board.data?.techs ?? [], [board.data]);
  const availByTech = useMemo(() => {
    const map = new Map<number, ScheduleAvailabilityBlock[]>();
    for (const a of avail.data ?? []) {
      const arr = map.get(a.technicianId) ?? [];
      arr.push(a);
      map.set(a.technicianId, arr);
    }
    return map;
  }, [avail.data]);

  // Visible time window: widen to fit any early/late job or timed availability.
  const win = useMemo(() => {
    const all: Array<{ start: string; end: string; allDay?: boolean }> = [];
    for (const t of techs) {
      for (const b of t.blocks) {
        if (b.source !== "B_TAVAILABILITY") all.push({ start: b.start, end: b.end, allDay: b.all_day });
      }
    }
    for (const a of avail.data ?? []) all.push({ start: a.start, end: a.end, allDay: a.allDay });
    return dayWindow(all);
  }, [techs, avail.data]);

  const colH = colHeight(win.startHour, win.endHour);
  const labels = hourLabels(win.startHour, win.endHour);

  const refetchAvail = (): void => {
    // Both Day and Month avail queries share the "schedule" / availability
    // family — broad invalidate covers either active mode.
    void qc.invalidateQueries({ queryKey: ["schedule", "avail", date] });
    void qc.invalidateQueries({ queryKey: ["schedule", "monthAvail"] });
  };

  return (
    <div className="flex h-full flex-col bg-ink-800">
      <TopNav
        userName={userName}
        status={board.isLoading ? <span className="text-[13px]">loading…</span> : null}
      />

      {/* Date nav */}
      <div className="flex flex-wrap items-center gap-3 border-b border-ink-700 px-6 py-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() =>
              setDate((d) =>
                viewMode === "day" ? shiftDate(d, -1) : shiftMonthFirst(d, -1)
              )
            }
            className="rounded-md border border-ink-500 px-2.5 py-1.5 text-[13px] text-ink-50 hover:bg-ink-700"
            title={viewMode === "day" ? "Previous day" : "Previous month"}
          >
            ‹
          </button>
          <button
            onClick={() =>
              setDate(viewMode === "day" ? todayIso() : monthStartIso(todayIso()))
            }
            className="rounded-md border border-ink-500 px-3 py-1.5 text-[13px] text-ink-50 hover:bg-ink-700"
          >
            Today
          </button>
          <button
            onClick={() =>
              setDate((d) =>
                viewMode === "day" ? shiftDate(d, 1) : shiftMonthFirst(d, 1)
              )
            }
            className="rounded-md border border-ink-500 px-2.5 py-1.5 text-[13px] text-ink-50 hover:bg-ink-700"
            title={viewMode === "day" ? "Next day" : "Next month"}
          >
            ›
          </button>
        </div>
        <input
          type="date"
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          className="rounded-md border border-ink-500 bg-ink-700 px-3 py-1.5 text-[13px] text-ink-50"
        />
        <div className="text-[15px] font-semibold text-ink-50">
          {viewMode === "day" ? fmtLongDate(date) : fmtMonthYear(date)}
        </div>

        {/* Day / Month toggle */}
        <div className="flex overflow-hidden rounded-md border border-ink-500">
          {(["day", "month"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setViewMode(m)}
              className={`px-3 py-1.5 text-[13px] ${
                viewMode === m
                  ? "bg-emerald-700 text-ink-50"
                  : "bg-ink-700 text-ink-100 hover:bg-ink-600"
              }`}
            >
              {m === "day" ? "Day" : "Month"}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-3 text-[12px] text-ink-100">
          <Legend swatch="bg-emerald-700/70" label="Job" />
          <Legend swatch="bg-red-600/80" label="Off (full day)" />
          <Legend swatch="bg-amber-400/85" label="Off (partial)" />
          <Legend swatch="bg-slate-600/50" label="Busy (calendar)" />
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto p-4">
        {board.isError ? (
          <div className="text-[15px] text-slack-pink">
            Couldn't load the schedule: {(board.error as Error).message}
          </div>
        ) : techs.length === 0 && !board.isLoading ? (
          <div className="text-[15px] text-ink-100">No active technicians.</div>
        ) : viewMode === "month" ? (
          <MonthGrid
            techs={techs}
            availability={avail.data ?? []}
            monthFromIso={monthFrom}
            dayCount={monthDayCount}
            onCellClick={(iso) => {
              setDate(iso);
              setViewMode("day");
            }}
          />
        ) : (
          <div className="inline-flex min-w-full">
            {/* Time-label column */}
            <div style={{ width: TIME_COL_W }} className="shrink-0">
              <div style={{ height: HEADER_H }} />
              <div className="relative" style={{ height: colH }}>
                {labels.map((h) => (
                  <div
                    key={h}
                    className="absolute right-1 -translate-y-1/2 text-[11px] tabular-nums text-ink-100"
                    style={{ top: (h - win.startHour) * 60 }}
                  >
                    {pad2(h)}:00
                  </div>
                ))}
              </div>
            </div>

            {/* Tech columns */}
            {techs.map((t) => (
              <TechColumn
                key={t.tech_id}
                tech={t}
                availability={availByTech.get(t.tech_id) ?? []}
                startHour={win.startHour}
                endHour={win.endHour}
                colH={colH}
                labels={labels}
                onJobClick={(block) => setJobPopover({ block })}
                onAddOff={() => setAddFor(t)}
                onRemoveAvail={async (id) => {
                  if (!window.confirm("Remove this time-off block?")) return;
                  await deleteScheduleAvailability(id);
                  refetchAvail();
                }}
              />
            ))}
          </div>
        )}
      </div>

      {jobPopover && (
        <JobPopover
          block={jobPopover.block}
          onClose={() => setJobPopover(null)}
          onOpenCard={async (vid) => {
            const cardId = await resolveVisitCard(vid);
            setJobPopover(null);
            if (cardId !== null) {
              setOpenCardId(cardId);
            } else {
              window.open(`${LEGACY_ORDER_BASE}${vid}`, "_blank", "noopener");
            }
          }}
        />
      )}

      {addFor && (
        <AddAvailabilityModal
          tech={addFor}
          date={date}
          onClose={() => setAddFor(null)}
          onSaved={() => {
            setAddFor(null);
            refetchAvail();
          }}
        />
      )}

      {openCardId !== null && cardQuery.data && (
        <Slideout card={cardQuery.data as CardView} onClose={() => setOpenCardId(null)} />
      )}
    </div>
  );
}

// ===========================================================================
// Month grid — techs as rows, days of the selected month as columns.
//
// Each cell shows a compact summary for that (tech, day): a green badge with
// the job count, a red bar if any unavailability covers part of the day, a
// slate bar for calendar busy. Clicking a cell switches the page to the Day
// view at that date.
//
// Data: one call to /api/dispatch/board?days={daysInMonth} + one call to
// /api/schedule/availability for the same window — no per-day round-trips.
// ===========================================================================

function MonthGrid({
  techs,
  availability,
  monthFromIso,
  dayCount,
  onCellClick,
}: {
  techs: DispatchTech[];
  availability: ScheduleAvailabilityBlock[];
  monthFromIso: string;
  dayCount: number;
  onCellClick: (iso: string) => void;
}): JSX.Element {
  const todayStr = todayIso();
  const monthStart = parseIsoDate(monthFromIso);

  // Pre-bucket jobs and avail by (tech_id, YYYY-MM-DD) for O(1) cell lookup.
  type CellAgg = {
    jobs: DispatchBlock[];
    calendarBusy: DispatchBlock[];
    techAvail: DispatchBlock[];
    editableAvail: ScheduleAvailabilityBlock[];
  };
  const cells = useMemo(() => {
    const m = new Map<string, CellAgg>();
    const ensure = (k: string): CellAgg => {
      let v = m.get(k);
      if (!v) {
        v = { jobs: [], calendarBusy: [], techAvail: [], editableAvail: [] };
        m.set(k, v);
      }
      return v;
    };
    for (const t of techs) {
      for (const b of t.blocks) {
        const day = b.start.slice(0, 10);
        const cell = ensure(`${t.tech_id}|${day}`);
        if (b.kind === "visit") cell.jobs.push(b);
        else if (b.source === "B_TAVAILABILITY") cell.techAvail.push(b);
        else cell.calendarBusy.push(b);
      }
    }
    for (const a of availability) {
      const day = a.start.slice(0, 10);
      ensure(`${a.technicianId}|${day}`).editableAvail.push(a);
    }
    return m;
  }, [techs, availability]);

  const dayHeaders = useMemo(() => {
    return Array.from({ length: dayCount }, (_, i) => {
      const d = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1 + i);
      const iso = isoDate(d);
      return {
        iso,
        dayOfMonth: d.getDate(),
        weekday: d.toLocaleDateString("en-GB", { weekday: "narrow" }), // M T W T F S S
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        isToday: iso === todayStr,
      };
    });
  }, [monthStart, dayCount, todayStr]);

  const CELL_W = 48; // wide enough for a 4-char outward like "SW15"
  const NAME_W = 180;

  return (
    <div className="inline-block min-w-full">
      <table className="border-collapse text-[12px] text-ink-50">
        <thead className="sticky top-0 z-10">
          <tr>
            <th
              className="sticky left-0 z-20 border-b border-r border-ink-500 bg-ink-900 px-2 py-2 text-left text-[13px] font-semibold"
              style={{ minWidth: NAME_W }}
            >
              Technician
            </th>
            {dayHeaders.map((d) => (
              <th
                key={d.iso}
                className={`border-b border-r border-ink-500 px-1 py-2 text-center font-medium ${
                  d.isToday
                    ? "bg-emerald-900/60 text-emerald-100"
                    : d.isWeekend
                      ? "bg-ink-900/60 text-ink-100"
                      : "bg-ink-900 text-ink-50"
                }`}
                style={{ minWidth: CELL_W, width: CELL_W }}
              >
                <div className="text-[10px] text-ink-100">{d.weekday}</div>
                <div className="text-[12px] tabular-nums">{d.dayOfMonth}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {techs.map((t) => (
            <tr key={t.tech_id} className="hover:bg-ink-800/40">
              <th
                className="sticky left-0 z-10 border-b border-r border-ink-500 bg-ink-900 px-2 py-1 text-left text-[13px] font-medium text-ink-50"
                style={{ minWidth: NAME_W }}
              >
                {t.tech_name}
                {t.tech_commission_pct != null && (
                  <span className="ml-1 text-[11px] text-ink-100">
                    ({t.tech_commission_pct}%)
                  </span>
                )}
              </th>
              {dayHeaders.map((d) => {
                const cell = cells.get(`${t.tech_id}|${d.iso}`);
                const offClass = classifyUnavailability(cell);
                const calBusy = cell?.calendarBusy.length ?? 0;
                const title = buildCellTitle(t.tech_name, d.iso, cell);
                const outwards = (cell?.jobs ?? []).map((j) =>
                  postcodeOutward(j.postcode)
                );
                // Bar colour: red = whole working day off; yellow = partial off;
                // slate = no off but calendar shows busy. Tooltip carries full
                // detail, so the bar just needs to disambiguate at a glance.
                const barClass =
                  offClass === "full"
                    ? "bg-red-600/80"
                    : offClass === "partial"
                      ? "bg-amber-400/85"
                      : calBusy > 0
                        ? "bg-slate-500/80"
                        : null;
                return (
                  <td
                    key={d.iso}
                    className={`group cursor-pointer border-b border-r border-ink-500 align-top hover:bg-ink-700/60 ${
                      d.isToday ? "bg-emerald-900/20" : d.isWeekend ? "bg-ink-900/30" : ""
                    }`}
                    style={{ minWidth: CELL_W, width: CELL_W }}
                    onClick={() => onCellClick(d.iso)}
                    title={title}
                  >
                    <div className="flex min-h-[28px] flex-col gap-0.5 px-1 py-1">
                      {outwards.map((pc, i) => (
                        <div
                          key={i}
                          className="truncate rounded bg-emerald-700/80 px-1 text-center text-[11px] font-semibold leading-4 text-emerald-50"
                        >
                          {pc}
                        </div>
                      ))}
                      {barClass && (
                        <div className={`mt-auto h-1.5 rounded ${barClass}`} />
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 text-[11px] text-ink-100">
        Click any cell to jump to that day's detailed view.
      </div>
    </div>
  );
}

// "NW7 1FX" → "NW7"; pass-through for already-bare outwards. Empty for null.
function postcodeOutward(pc: string | null): string {
  if (!pc) return "·";
  const s = pc.trim().toUpperCase();
  const sp = s.indexOf(" ");
  return sp > 0 ? s.slice(0, sp) : s;
}

/** Classify a tech's unavailability on one day. Distinguishes "all-day off"
 *  (red bar) from "off only for part of the working day" (yellow bar) so the
 *  Month grid is readable at a glance without hovering every cell. */
function classifyUnavailability(
  cell:
    | {
        techAvail: DispatchBlock[];
        editableAvail: ScheduleAvailabilityBlock[];
      }
    | undefined
): "none" | "partial" | "full" {
  if (!cell) return "none";
  const techAvail = cell.techAvail;
  const editable = cell.editableAvail;
  if (techAvail.length === 0 && editable.length === 0) return "none";
  // Any all-day flag → full.
  if (techAvail.some((b) => b.all_day) || editable.some((e) => e.allDay)) {
    return "full";
  }
  // Otherwise, union the off intervals against the 09:00–17:00 working
  // window. Coverage within 30 min of the full 8h workday = "full".
  const WORK_START = 9 * 60;
  const WORK_END = 17 * 60;
  const intervals: Array<{ s: number; e: number }> = [];
  const toMin = (iso: string): number => {
    const t = iso.split("T")[1] ?? "00:00:00";
    const [h, m] = t.split(":").map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };
  for (const b of techAvail) {
    intervals.push({
      s: Math.max(WORK_START, toMin(b.start)),
      e: Math.min(WORK_END, toMin(b.end)),
    });
  }
  for (const e of editable) {
    intervals.push({
      s: Math.max(WORK_START, toMin(e.start)),
      e: Math.min(WORK_END, toMin(e.end)),
    });
  }
  const usable = intervals.filter((i) => i.e > i.s);
  if (usable.length === 0) return "partial";
  usable.sort((a, b) => a.s - b.s);
  let covered = 0;
  let curS = usable[0]!.s;
  let curE = usable[0]!.e;
  for (let i = 1; i < usable.length; i++) {
    const cur = usable[i]!;
    if (cur.s <= curE) {
      curE = Math.max(curE, cur.e);
    } else {
      covered += curE - curS;
      curS = cur.s;
      curE = cur.e;
    }
  }
  covered += curE - curS;
  return covered >= (WORK_END - WORK_START) - 30 ? "full" : "partial";
}

function buildCellTitle(
  techName: string,
  iso: string,
  cell:
    | {
        jobs: DispatchBlock[];
        calendarBusy: DispatchBlock[];
        techAvail: DispatchBlock[];
        editableAvail: ScheduleAvailabilityBlock[];
      }
    | undefined
): string {
  const lines: string[] = [`${techName} · ${iso}`];
  if (!cell) {
    lines.push("(nothing scheduled)");
    return lines.join("\n");
  }
  for (const j of cell.jobs) {
    const t = j.all_day ? "all day" : `${isoHM(j.start)}–${isoHM(j.end)}`;
    lines.push(`• Job: ${j.customer_name ?? "(no name)"} · ${j.postcode ?? "?"} · ${t}`);
  }
  for (const a of cell.techAvail) {
    const t = a.all_day ? "all day" : `${isoHM(a.start)}–${isoHM(a.end)}`;
    lines.push(`• Off (tech app): ${t}`);
  }
  for (const e of cell.editableAvail) {
    const t = e.allDay ? "all day" : `${isoHM(e.start)}–${isoHM(e.end)}`;
    lines.push(`• Off: ${t}`);
  }
  for (const c of cell.calendarBusy) {
    const t = c.all_day ? "all day" : `${isoHM(c.start)}–${isoHM(c.end)}`;
    lines.push(`• Busy (calendar): ${t}`);
  }
  return lines.join("\n");
}

function Legend({ swatch, label }: { swatch: string; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-2.5 w-2.5 rounded-sm ${swatch}`} />
      {label}
    </span>
  );
}

// ─── Tech column ────────────────────────────────────────────────────────────

function TechColumn({
  tech,
  availability,
  startHour,
  endHour,
  colH,
  labels,
  onJobClick,
  onAddOff,
  onRemoveAvail,
}: {
  tech: DispatchTech;
  availability: ScheduleAvailabilityBlock[];
  startHour: number;
  endHour: number;
  colH: number;
  labels: number[];
  onJobClick: (b: DispatchBlock) => void;
  onAddOff: () => void;
  onRemoveAvail: (id: number) => void | Promise<void>;
}): JSX.Element {
  const jobs = tech.blocks.filter((b) => b.source === "B_TVISIT");
  const freebusy = tech.blocks.filter((b) => b.source === "B_TFREEBUSY");

  return (
    <div style={{ minWidth: COL_MIN_W }} className="shrink-0 border-l border-ink-700">
      {/* Header */}
      <div
        style={{ height: HEADER_H }}
        className="flex flex-col justify-center rounded-t-md border border-ink-600 bg-ink-700 px-2"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="truncate text-[14px] font-semibold text-ink-50" title={tech.tech_name}>
            {tech.tech_name}
          </div>
          <button
            onClick={onAddOff}
            className="shrink-0 rounded-md border border-ink-500 px-1.5 py-0.5 text-[11px] text-ink-50 hover:bg-ink-600"
            title="Add time off for this cleaner"
          >
            + off
          </button>
        </div>
        {tech.home_postcode ? (
          <div className="truncate text-[12px] text-ink-100">{tech.home_postcode}</div>
        ) : null}
      </div>

      {/* Body */}
      <div className="relative border border-t-0 border-ink-600 bg-ink-900" style={{ height: colH }}>
        {labels.slice(1).map((h) => (
          <div
            key={h}
            className="pointer-events-none absolute left-0 right-0 border-t border-ink-700"
            style={{ top: (h - startHour) * 60 }}
          />
        ))}

        {/* Paint order = stacking order: availability (back), then calendar
            busy, then jobs on TOP so admins can click a job that overlaps a
            busy block. */}
        {availability.map((a) => (
          <AvailBlock key={`a${a.id}`} block={a} startHour={startHour} endHour={endHour} onRemove={() => void onRemoveAvail(a.id)} />
        ))}
        {freebusy.map((b, i) => (
          <FreeBusyBlock key={`f${i}`} block={b} startHour={startHour} endHour={endHour} />
        ))}
        {jobs.map((b, i) => (
          <JobBlock key={`j${i}`} block={b} startHour={startHour} endHour={endHour} onClick={() => onJobClick(b)} />
        ))}
      </div>
    </div>
  );
}

function statusColour(status: string | null): string {
  const s = (status ?? "").toLowerCase();
  if (s.includes("onsite estimate")) return "bg-pink-700/70 border-pink-600 text-pink-50 hover:bg-pink-700/90";
  if (s.includes("closed")) return "bg-amber-700/70 border-amber-600 text-amber-50 hover:bg-amber-700/90";
  // Booking made / Revisit required → blue
  return "bg-sky-700/70 border-sky-600 text-sky-50 hover:bg-sky-700/90";
}

function JobBlock({
  block,
  startHour,
  endHour,
  onClick,
}: {
  block: DispatchBlock;
  startHour: number;
  endHour: number;
  onClick: () => void;
}): JSX.Element | null {
  const pos = clampToCol(
    minutesFromDayStart(block.start, startHour),
    minutesFromDayStart(block.end, startHour),
    startHour,
    endHour
  );
  if (!pos) return null;
  return (
    <button
      onClick={onClick}
      style={{ top: pos.top, height: pos.height }}
      className={`absolute left-0.5 right-0.5 overflow-hidden rounded border px-1 py-0.5 text-left ${statusColour(block.status)}`}
      title={`#${block.vid} · ${block.customer_name ?? ""} · ${block.postcode ?? ""} · £${block.value_gbp?.toFixed(0) ?? "?"}`}
    >
      <div className="truncate text-[12px] font-medium">
        {block.customer_name ?? `Job #${block.vid}`}
      </div>
      <div className="truncate text-[12px] opacity-80">{block.postcode ?? ""}</div>
      <div className="text-[11px] tabular-nums opacity-70">
        {isoHM(block.start)}–{isoHM(block.end)}
      </div>
    </button>
  );
}

function FreeBusyBlock({
  block,
  startHour,
  endHour,
}: {
  block: DispatchBlock;
  startHour: number;
  endHour: number;
}): JSX.Element | null {
  const pos = clampToCol(
    minutesFromDayStart(block.start, startHour),
    minutesFromDayStart(block.end, startHour),
    startHour,
    endHour
  );
  if (!pos) return null;
  return (
    <div
      style={{ top: pos.top, height: pos.height }}
      className="absolute left-0.5 right-0.5 overflow-hidden rounded border border-slate-500/70 bg-slate-600/40 px-1 py-0.5 text-slate-100"
      title="Busy (external calendar)"
    >
      <div className="truncate text-[12px]">Busy (calendar)</div>
      <div className="text-[11px] tabular-nums opacity-70">
        {block.all_day ? "all day" : `${isoHM(block.start)}–${isoHM(block.end)}`}
      </div>
    </div>
  );
}

function AvailBlock({
  block,
  startHour,
  endHour,
  onRemove,
}: {
  block: ScheduleAvailabilityBlock;
  startHour: number;
  endHour: number;
  onRemove: () => void;
}): JSX.Element | null {
  const pos = clampToCol(
    minutesFromDayStart(block.start, startHour),
    minutesFromDayStart(block.end, startHour),
    startHour,
    endHour
  );
  if (!pos) return null;
  return (
    <div
      style={{ top: pos.top, height: pos.height }}
      className="absolute left-0.5 right-0.5 overflow-hidden rounded border border-red-700/70 bg-red-700/40 px-1 py-0.5 text-red-100"
      title="Unavailable (tech app / admin)"
    >
      <button
        onClick={onRemove}
        className="absolute right-0.5 top-0.5 rounded px-1 text-[11px] leading-none text-red-100 hover:bg-red-700/60"
        title="Remove this time off"
      >
        ✕
      </button>
      <div className="truncate text-[12px]">Off</div>
      {block.location ? <div className="truncate text-[11px] opacity-80">{block.location}</div> : null}
      <div className="text-[11px] tabular-nums opacity-70">
        {block.allDay ? "all day" : `${isoHM(block.start)}–${isoHM(block.end)}`}
      </div>
    </div>
  );
}

// ─── Job detail popover ──────────────────────────────────────────────────────

function JobPopover({
  block,
  onClose,
  onOpenCard,
}: {
  block: DispatchBlock;
  onClose: () => void;
  onOpenCard: (vid: number) => void | Promise<void>;
}): JSX.Element {
  const [opening, setOpening] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl border border-ink-700 bg-ink-800 p-5 shadow-card" onClick={(e) => e.stopPropagation()}>
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-100">Job #{block.vid}</div>
        <h2 className="mt-1 text-xl font-semibold text-ink-50">{block.customer_name ?? "(no name)"}</h2>
        <div className="mt-3 space-y-1 text-[14px] text-ink-50">
          <Row label="When" value={`${isoHM(block.start)}–${isoHM(block.end)}`} />
          <Row label="Postcode" value={block.postcode ?? "—"} />
          <Row label="Status" value={block.status ?? "—"} />
          <Row label="Value" value={block.value_gbp != null ? `£${block.value_gbp.toFixed(2)}` : "—"} />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700">
            Close
          </button>
          <button
            onClick={async () => {
              if (block.vid == null) return;
              setOpening(true);
              try {
                await onOpenCard(block.vid);
              } finally {
                setOpening(false);
              }
            }}
            disabled={opening || block.vid == null}
            className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
          >
            {opening ? "Opening…" : "Open card"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-ink-100">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

// ─── Add availability modal ──────────────────────────────────────────────────

function AddAvailabilityModal({
  tech,
  date,
  onClose,
  onSaved,
}: {
  tech: DispatchTech;
  date: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [location, setLocation] = useState("");

  const m = useMutation({
    mutationFn: () =>
      createScheduleAvailability({
        technicianId: tech.tech_id,
        date,
        allDay,
        startTime: allDay ? undefined : startTime,
        endTime: allDay ? undefined : endTime,
        location: location.trim() || undefined,
      }),
    onSuccess: onSaved,
  });

  const valid = allDay || startTime < endTime;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-ink-700 bg-ink-800 p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-semibold text-ink-50">Add time off</h2>
        <p className="mt-1 text-[14px] text-ink-100">
          {tech.tech_name} · {date}
        </p>
        <label className="mt-4 flex cursor-pointer items-center gap-2 text-[15px] text-ink-50">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="h-4 w-4 accent-slack-aubergine" />
          All day
        </label>
        {!allDay && (
          <div className="mt-3 flex items-center gap-2">
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="rounded-md border border-ink-500 bg-ink-700 px-2 py-1.5 text-[15px] text-ink-50" />
            <span className="text-ink-100">to</span>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="rounded-md border border-ink-500 bg-ink-700 px-2 py-1.5 text-[15px] text-ink-50" />
          </div>
        )}
        <div className="mt-3">
          <label className="block text-[13px] uppercase tracking-wider text-ink-100">Location (optional)</label>
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. N12" className="mt-1 w-full rounded-md border border-ink-500 bg-ink-700 px-3 py-2 text-[15px] text-ink-50" />
        </div>
        {m.isError && <div className="mt-3 text-[13px] text-slack-pink">{(m.error as Error).message}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-ink-500 px-4 py-2 text-[15px] text-ink-50 hover:bg-ink-700">
            Cancel
          </button>
          <button
            onClick={() => m.mutate()}
            disabled={!valid || m.isPending}
            className="rounded-md bg-slack-aubergine px-4 py-2 text-[15px] font-semibold text-ink-50 hover:bg-slack-aubergineLight disabled:opacity-50"
          >
            {m.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
